import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, Modal, StatusBadge, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { encodeId } from '../../utils/idCodec';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { Employee } from '@kss/api';
import { employeeService } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';

export default function AllEmployees() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const perms = useModulePerms('Employee');
  const [rows, setRows] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [detailRow, setDetailRow] = useState<Employee | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await employeeService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load employees';
      setError(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    if (!confirmDelete?.employeeId) return;
    setDeleting(true);
    try {
      await employeeService.delete(confirmDelete.employeeId);
      toast.success('Deleted', `${confirmDelete.firstName} removed`);
      setConfirmDelete(null); await load();
    } catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const fullName = (r: Employee) => [r.firstName, r.lastName].filter(Boolean).join(' ');

  const columns: DataGridColumn<Employee>[] = [
    // Live API field is `employeeCode` (not `code`); the legacy
    // accessor always rendered blank. The `salary` column is gone
    // — backend dropped that field along with `companyId`/`address`
    // when it migrated to the canonical Employee schema.
    {
      accessor: 'employeeCode',
      title: 'Code',
      sortable: true,
      filterable: true,
      width: 120,
      render: (r) => (r as Employee & { employeeCode?: string }).employeeCode || '—',
    },
    { accessor: 'firstName', title: 'Name', sortable: true, filterable: true, render: (r) => fullName(r) },
    { accessor: 'email', title: 'Email', sortable: true, filterable: true, render: (r) => r.email || '—' },
    { accessor: 'designation', title: 'Designation', sortable: true, filterable: true, render: (r) => r.designation || '—' },
    { accessor: 'department', title: 'Department', sortable: true, filterable: true, render: (r) => r.department || '—' },
    {
      // EmployeeStatus enum (1=Active, 2=OnLeave, 3=Resigned, 4=Terminated).
      // Older code mapped to `isActive` which the live row doesn't carry.
      accessor: 'status',
      title: 'Status',
      sortable: true,
      render: (r) => {
        const s = (r as Employee & { status?: number }).status;
        const tok = s === 1 ? 'active' : s === 2 ? 'pending' : s === 3 ? 'cancelled' : s === 4 ? 'cancelled' : 'inactive';
        return <StatusBadge status={tok} />;
      },
    },
    { accessor: 'employeeId', title: 'Actions', width: 110,
      render: (r) => (
        <div className="flex items-center gap-1">
          {perms.canUpdate && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); if (r.employeeId) navigate(`/panel/employees/${encodeId(r.employeeId)}`); }} title="Edit">
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
          {perms.canDelete && (
            <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      ) },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<Employee>
        title={<span className="hidden sm:inline">All employees</span>}
        columnPicker onRowClick={(row) => setDetailRow(row)} data={rows} columns={columns}
        selectable selectedIds={selected} onSelectedChange={setSelected}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <StandardAddDropdown basePath="/panel/employees" newLabel="New employee" service={employeeService} templateFields={['employeeCode', 'firstName', 'lastName', 'email', 'phone', 'designation', 'department', 'dateOfJoining', 'panNumber', 'aadhaarNumber', 'storeId', 'status']} module="Employee" />
          </div>
        }
      />
      <Modal open={!!detailRow} onClose={() => setDetailRow(null)} title={detailRow ? `Employee · ${fullName(detailRow)}` : ''} width={520}
        footer={<>
          <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
          {detailRow?.employeeId && (<Button onClick={() => { const id = detailRow.employeeId!; setDetailRow(null); navigate(`/panel/employees/${encodeId(id)}`); }}>Edit</Button>)}
        </>}>
        {detailRow && (
          <div className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4 text-sm">
            <DetailRow label="Code" value={detailRow.code} />
            <DetailRow label="Name" value={fullName(detailRow)} />
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Email" value={detailRow.email} />
              <DetailRow label="Phone" value={detailRow.phone} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Designation" value={detailRow.designation} />
              <DetailRow label="Department" value={detailRow.department} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Joining date" value={detailRow.joiningDate ? new Date(detailRow.joiningDate).toLocaleDateString() : ''} />
              <DetailRow label="Salary" value={`₹ ${Number(detailRow.salary ?? 0).toLocaleString('en-IN')}`} />
            </div>
            <DetailRow label="Address" value={detailRow.address} />
            <DetailRow label="Status" value={<StatusBadge status={detailRow.isActive ? 'active' : 'inactive'} />} />
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete} title="Delete employee"
        message={confirmDelete ? `Remove "${fullName(confirmDelete)}"? This cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}
